import invert from 'lodash/invert';
import rollbar from '~/vendor/rollbar';
import ProductTypes from '~/constants/types/ProductTypes';
import mms from '~/vendor/mms';
import {createAction} from 'redux-actions';

// Actions
import {
  discontinuedProduct,
  discontinuedProductColor,
  sleevePrintRemoveDesign
} from '~/actions/AnalyticsActions';
import {
  fetchStyle,
  fetchStyleFromColor,
  styleLoadError
} from '~/actions/CatalogActions';
import {loadComplements} from '~/actions/ComplementsActions';
import {
  updateSleevePrintOverride,
  setSleevePrintOverride
} from '~/actions/ConfigActions';
import {remove} from '~/actions/IconActions';
import {selectIcons} from '~/actions/SelectionActions';
import {changeView} from '~/actions/CanvasActions';
import {addProductToFundraising} from '~/actions/FundraisingActions';
import * as NotificationActions from '~/actions/NotificationActions';

// Selectors
import {
  getColor,
  getSizesForColor,
  getStyle,
  getSizesWithStatusForStyleAndColorIds
} from '~/selectors/CatalogSelectors';
import {
  getAnchorStyleId,
  getStyleCategoryPath,
  getColorSelectionsForStyle,
  getCurrentSelection,
  getSelectedColors,
  getSelectedProducts,
  getSelections,
  getViewConstraint,
  getViewsForStyle,
  getProductViewIcons,
  getRemainingProductView,
  getSelectedView,
  hasSleeveProductImageIssue,
  isSingleStyle,
  remainingProductsHaveSleeveProductImageIssue,
  getDecorationOverrideColor,
  getDroppedViewInfo
} from '~/selectors/ProductSelectors';
import {getQuantitiesJs} from '~/selectors/QuoteSelectors';
import {
  getCurrentDesignId,
  getIconsArray,
  hasSleeveDesign,
  viewIcons
} from '~/selectors/CanvasSelectors';

// Data
import {viewDimensions} from '~/data/accessors/canvas';

// Utils
import {getInkMatchError} from './ProductActions.util';
import {getProductKeys, sanitizeUrl} from '~/utils/url';
import {isSleeveView} from '~/utils/product';
import {isConsistentStyleCategoryPath} from '~/utils/catalog';

// Constants
import {ID_VIEW_MAP} from '~/constants/ProductConstants';
import * as NotificationConstants from '~/constants/NotificationConstants';
import {
  DEFAULT_COLOR_ID,
  DEFAULT_STYLE_ID,
  DEFAULT_CATEGORY_PATH,
  SLEEVE_VIEW_IDS,
  VIEW_ID_MAP
} from '~/constants/ProductConstants';
import {decorationOverridesEnabled} from '~/selectors/ConfigSelectors';
import {updateIconColorsBasedOnDecorationOverride} from './DecorationOverrides';
import {updateDroppedView} from './QuoteActions';

// Product Actions
export const replaceProducts = createAction(ProductTypes.REPLACE_PRODUCTS);

export function findDroppedSizes(
  previousColorId,
  nextStyleId,
  nextColorIds,
  state
) {
  // Build an array of droppedSizes based on quantities and validSizes
  const droppedSizes = Object.keys(nextColorIds).reduce((sizes, prevColor) => {
    const nextColorId = nextColorIds[prevColor];
    const quantities = getQuantitiesJs(state);
    const prevColorQuantities = quantities[prevColor] || {};
    const nextColorSizes = getSizesForColor(state, {
      colorId: nextColorId,
      styleId: nextStyleId
    });

    const nextColorSizesWithStatus = getSizesWithStatusForStyleAndColorIds(
      state
    )(nextStyleId, nextColorId);
    const sizesThisColor = Object.keys(prevColorQuantities)
      .filter(
        size => !nextColorSizes.includes(size) && prevColorQuantities[size] > 0
      )
      .map(size => {
        const quantity = prevColorQuantities[size];
        const droppedSize = {quantity, size};
        const sizeWithStatus = nextColorSizesWithStatus?.find(
          sizeInfo => sizeInfo.name === size
        );
        if (sizeWithStatus) {
          droppedSize.status = sizeWithStatus.status;
        }
        return droppedSize;
      });
    return sizes.concat(sizesThisColor);
  }, []);
  return droppedSizes;
}

export function addProduct({
  metricsEnabled,
  overrideNotification = false,
  products,
  urlPath,
  isMerging = false
}) {
  if (!products?.length) {
    rollbar.warn('AddProduct action called without any products');
  }
  return (dispatch, getState) => {
    const state = getState();
    const previousStyleId = getAnchorStyleId(state);
    const canvasViewIcons = viewIcons(state);
    const nextStyleId = products[0].styleId;
    const style = getStyle(state, {styleId: nextStyleId});

    // we are geting views here that have designs that would be lost/retained
    // when adding a new product that does not contain the view
    const viewsWithDesigns = canvasViewIcons.keySeq().toJS();
    const newStyleViews = style.views;
    const lostViewsWithIcons = viewsWithDesigns.filter(
      v => !newStyleViews.find(view => view.id === v)
    );
    const retainedViewsWithIcons = viewsWithDesigns.filter(v =>
      newStyleViews.find(view => view.id === v)
    );

    if (lostViewsWithIcons?.length && !overrideNotification) {
      dispatch(
        NotificationActions.error({
          key: NotificationConstants.ADD_PRODUCT_MISSING_AREAS,
          payload: {
            retainedViewsWithIcons,
            metricsEnabled,
            productName: style.name || 'product',
            products,
            urlPath
          }
        })
      );

      return;
    }

    const oldViews = getViewsForStyle(state, {styleId: previousStyleId});
    const {sponsorship} = style;
    const views = getViewsForStyle(state, {styleId: nextStyleId});

    const oldConstraints = oldViews.reduce((constraintMap, view) => {
      constraintMap[view.id] = getViewConstraint(state)(view);
      return constraintMap;
    }, {});

    const constraints = views.reduce((constraintMap, view) => {
      if (SLEEVE_VIEW_IDS.includes(view.id)) {
        constraintMap[view.id] = view.constraint;
        return constraintMap;
      } else {
        constraintMap[view.id] = oldConstraints[view.id]
          ? oldConstraints[view.id]
          : view.constraint;
        return constraintMap;
      }
    }, {});

    const designDimensions = viewDimensions(canvasViewIcons);
    const newViewIcons = {};

    for (const dimension of Object.keys(designDimensions)) {
      if (constraints[dimension]) {
        newViewIcons[dimension] = designDimensions[dimension];
      }
    }

    const oldScales = views.reduce((scalesMap, view) => {
      scalesMap[view.id] = view.scale;
      return scalesMap;
    }, {});
    const scales = views.reduce((scalesMap, view) => {
      scalesMap[view.id] = view.scale;
      return scalesMap;
    }, {});

    dispatch({
      type: ProductTypes.ADD_PRODUCT,
      payload: {
        products: products.map(p => ({
          ...p,
          validSizes: getSizesForColor(getState(), p)
        })),
        constraints,
        newViewIcons,
        oldConstraints,
        sponsorship,
        views,
        isMerging,
        scales,
        oldScales
      },
      meta: {
        newStyle: products[0].styleId !== getAnchorStyleId(getState()),
        metricsEnabled
      }
    });
    dispatch(updateSleevePrintOverride());
    products.forEach(product => {
      dispatch(addProductToFundraising(product));
      dispatch(loadComplements(product.styleId));
    });

    dispatch(
      updateCategoryPaths({
        styleId: nextStyleId,
        primaryCategoryId: style.primary_category_id
      })
    );
  };
}

export function updateStylesCategoryPaths(styles) {
  return async (dispatch, getState) => {
    const styleUpdateRequests = {};
    styles?.forEach(styleId => {
      const style = getStyle(getState(), {styleId});
      if (style?.id && !styleUpdateRequests[style.id]) {
        styleUpdateRequests[style.id] = style.id;
        dispatch(
          updateCategoryPaths({
            styleId: style?.id,
            primaryCategoryId: style?.primary_category_id
          })
        );
      }
    });
  };
}

export function updateCategoryPaths({styleId, primaryCategoryId}) {
  return async (dispatch, getState) => {
    if (
      !styleId ||
      !primaryCategoryId ||
      isConsistentStyleCategoryPath({
        categoryPath: getStyleCategoryPath(getState(), styleId),
        primaryCategoryId
      })
    ) {
      return;
    }

    const category = await mms.getCategory(primaryCategoryId);
    const parentCategory = await mms.getCategory(category?.parent_id);
    const parentCategoryName = sanitizeUrl(parentCategory?.name);
    const categoryName = sanitizeUrl(category?.name);

    if (!parentCategoryName || !categoryName) return;

    dispatch({
      type: ProductTypes.UPDATE_CATEGORY_PATHS,
      payload: {
        styleId,
        categoryPath: `/${parentCategoryName}/${categoryName}/${primaryCategoryId}`
      }
    });
  };
}

export function changeColor(
  previousColorId,
  nextColorId,
  fireAnalytics = true,
  hideChooseAnotherItemCTA = false
) {
  return async (dispatch, getState) => {
    let state = getState();
    let nextStyleId = getCurrentSelection(state)?.styleId;

    if (!nextStyleId) return;

    let color = getColor(state, {colorId: nextColorId, styleId: nextStyleId});

    if (!color) {
      try {
        const previousStyleId = nextStyleId;
        const nextStyle = await dispatch(fetchStyleFromColor(nextColorId));
        nextStyleId = nextStyle.id;
        await dispatch(
          swapProduct({
            previousColorId,
            previousStyleId,
            nextProduct: {
              styleId: nextStyleId,
              colorId: nextColorId
            }
          })
        );
        state = getState();
        color = getColor(state, {colorId: nextColorId, styleId: nextStyleId});
      } catch (err) {
        dispatch(styleLoadError(nextColorId, err));
        return;
      }
    }

    const nextColorIds = {[previousColorId]: nextColorId};
    const droppedSizes = findDroppedSizes(
      previousColorId,
      nextStyleId,
      nextColorIds,
      state
    );

    dispatch({
      type: ProductTypes.CHANGE_COLOR,
      payload: {
        previousColorId,
        nextColorId,
        validSizes: getSizesForColor(state, {
          styleId: getCurrentSelection(state)?.styleId,
          colorId: nextColorId
        })
      },
      meta: {
        fireAnalytics,
        confirmationRequired: droppedSizes.length,
        notification: {
          key: 'sizesUnavailableWarning',
          colorName: color.name,
          droppedSizes,
          hideChooseAnotherItemCTA
        }
      }
    });
    dispatch(
      addProductToFundraising({
        styleId: nextStyleId,
        colorId: nextColorIds[previousColorId]
      })
    );

    // we only want to apply any changes if the decoration override feature flag is enabled
    const isDecorationOverrideEnabled = decorationOverridesEnabled(state);
    if (isDecorationOverrideEnabled) {
      const currentDecoOverride = getDecorationOverrideColor(state);
      dispatch(
        updateIconColorsBasedOnDecorationOverride(Boolean(currentDecoOverride))
      );
    }
  };
}

export const confirmDeleteProduct = createAction(
  ProductTypes.DELETE_PRODUCT,
  payload => payload,
  (payload, meta) => meta || {fireAnalytics: true}
);

export function deleteProduct(payload) {
  return (dispatch, getState) => {
    const state = getState();

    if (
      hasSleeveDesign(state) &&
      remainingProductsHaveSleeveProductImageIssue(state)(payload?.colorId)
    ) {
      dispatch(
        NotificationActions.error({
          key: NotificationConstants.DELETE_SLEEVE_DESIGN_WARNING,
          payload: {
            handleConfirmDeletion: () => {
              dispatch(removeSleeveDesign());
              dispatch(confirmDeleteProduct(payload));
              dispatch(setSleevePrintOverride(false));
              dispatch(
                selectRemainingProductView({
                  droppedViewId: VIEW_ID_MAP.leftSleeve
                })
              );
            }
          }
        })
      );
    } else {
      dispatch(confirmDeleteProduct(payload));
    }
  };
}

const resetAction = createAction(ProductTypes.RESET);
export function reset(
  defaultStyleID = DEFAULT_STYLE_ID,
  defaultColorID = DEFAULT_COLOR_ID,
  defaultCategoryPath = DEFAULT_CATEGORY_PATH
) {
  return (dispatch, getState) => {
    const productKeys = getProductKeys();
    let {styleId = defaultStyleID, colorId = defaultColorID} = productKeys;
    if (!styleId) styleId = defaultStyleID;
    if (!colorId) colorId = defaultColorID;

    const sizesForColor = getSizesForColor(getState(), {styleId, colorId});
    if (sizesForColor.length) {
      dispatch(
        resetAction({
          colorId,
          styleId,
          categoryPath: defaultCategoryPath,
          sizes: sizesForColor
        })
      );
    } else {
      dispatch(fetchStyle(defaultStyleID))
        .then(style => {
          return (
            style &&
            dispatch(
              resetAction({
                colorId,
                styleId,
                categoryPath: defaultCategoryPath,
                sizes: style.sizes
              })
            )
          );
        })
        .catch(error => dispatch(styleLoadError(defaultStyleID, error)));
    }
  };
}

export const selectProduct = createAction(ProductTypes.SELECT_PRODUCT);

// This action replaces the product via the product reducer
// This action ensures proper scaling of the design via the icon reducer
export function replaceProductsCartMode(oldProducts, newProducts) {
  return (dispatch, getState) => {
    const state = getState();
    const previousStyleId = oldProducts.styleId;
    const nextStyleId = newProducts.styleId;
    const canvasViewIcons = viewIcons(state);
    const designDimensions = viewDimensions(canvasViewIcons);

    const views = getViewsForStyle(state, {styleId: nextStyleId});
    const oldViews = getViewsForStyle(state, {styleId: previousStyleId});

    const style = getStyle(state, {styleId: nextStyleId});
    const sponsorship = style.sponsorship;

    const constraints = views.reduce((constraintMap, view) => {
      constraintMap[view.id] = view.constraint;
      return constraintMap;
    }, {});

    const oldConstraints = oldViews.reduce((constraintMap, view) => {
      constraintMap[view.id] = view.constraint;
      return constraintMap;
    }, {});

    const newViewIcons = {};
    for (const dimension of Object.keys(designDimensions)) {
      if (constraints[dimension]) {
        newViewIcons[dimension] = designDimensions[dimension];
      }
    }

    dispatch({
      type: ProductTypes.REPLACE_PRODUCTS_CART_MODE,
      payload: {
        products: newProducts,
        views,
        sponsorship,
        constraints,
        oldConstraints,
        newViewIcons
      }
    });
  };
}

export function swapProduct({
  previousColorId,
  previousStyleId,
  nextProduct,
  isAD = false,
  clearMismatchedViews = false,
  clearSizeDropWarning = false,
  clearPreviousProducts = true, // true by default and this will clear all original selected products
  isSwappingEmbroidToPrintVariant = false,
  isInitialLabLoad = false,
  fireAnalytics = true,
  rearrangeIcons = true,
  useSpecialGuidelineOnIconRearranger = true,
  preventHighInkColorAlert = false,
  shouldColorMatch = true,
  redoUndoable = true
}) {
  return async (dispatch, getState) => {
    const colorsToSwap = getSelections(getState())
      .filter(
        s => s.colorId !== previousColorId && s.styleId === previousStyleId
      )
      .map(s => s.colorId)
      .toArray();

    // try to color match if multiple products are selection and the next product is not a specialty item
    if (
      shouldColorMatch &&
      colorsToSwap.length >= 1 &&
      !getStyle(getState(), nextProduct).specialty
    ) {
      await mms
        .getColorsInStyle(nextProduct.styleId, colorsToSwap)
        .then(doSwap);
    } else {
      doSwap();
    }

    function doSwap(colorMatches = {}) {
      const state = getState();

      // Gather info about the old style
      const oldViews = getViewsForStyle(state, {styleId: previousStyleId});
      const oldConstraints = oldViews.reduce((constraintMap, view) => {
        constraintMap[view.id] = view.constraint;
        return constraintMap;
      }, {});
      const oldScales = oldViews.reduce((scalesMap, view) => {
        scalesMap[view.id] = view.scale;
        return scalesMap;
      }, {});

      // Gather info about the new style
      const canvasViewIcons = viewIcons(state);
      const {styleId: nextStyleId, categoryPath: nextCategoryPath} =
        nextProduct;

      // make sure that matches don't result in duplicate products.
      Object.keys(colorMatches).forEach(nextColor => {
        if (
          !colorMatches[nextColor] ||
          colorMatches[nextColor] === nextProduct.colorId
        ) {
          delete colorMatches[nextColor];
        }
      });
      colorMatches = invert(invert(colorMatches)); // eliminate duplicate values

      const style = getStyle(state, {styleId: nextStyleId});

      const hasInkMatchWarning = getInkMatchError(style, state);
      if (hasInkMatchWarning) {
        // spawn notification and return, include info to call swapProduct again
        return dispatch(
          NotificationActions.error({
            key: NotificationConstants.PANTONE_DISABLED_WARNING,
            payload: {
              swapArgs: {
                previousColorId,
                previousStyleId,
                nextProduct,
                isAD,
                clearMismatchedViews,
                clearSizeDropWarning,
                clearPreviousProducts,
                isSwappingEmbroidToPrintVariant,
                isInitialLabLoad
              }
            }
          })
        );
      }

      const views = getViewsForStyle(state, {styleId: nextStyleId});
      const nextColorIds = {
        ...colorMatches,
        [previousColorId]: nextProduct.colorId
      };
      const validSizes = Object.keys(nextColorIds).reduce(
        (sizes, prevColor) => {
          const nextColorId = nextColorIds[prevColor];
          sizes[nextColorId] = getSizesForColor(state, {
            colorId: nextColorId,
            styleId: nextStyleId
          });
          return sizes;
        },
        {}
      );
      const constraints = views.reduce((constraintMap, view) => {
        constraintMap[view.id] = view.constraint;
        return constraintMap;
      }, {});
      const guidelines = views.reduce((guidelineMap, view) => {
        guidelineMap[view.id] = view.guidelines;
        return guidelineMap;
      }, {});
      const scales = views.reduce((scalesMap, view) => {
        scalesMap[view.id] = view.scale;
        return scalesMap;
      }, {});
      const designDimensions = viewDimensions(canvasViewIcons);
      const newViewIcons = {};
      for (const dimension of Object.keys(designDimensions)) {
        if (constraints[dimension]) {
          newViewIcons[dimension] = designDimensions[dimension];
        }
      }
      let droppedSizes = findDroppedSizes(
        previousColorId,
        nextStyleId,
        nextColorIds,
        state
      );
      const changedProductCategory = style?.primary_category_name;
      const decorationMethod = style?.decoration_method;
      const styleType = style?.style_type;
      const designId = getCurrentDesignId(state);
      const mismatchedAndMatched = checkDecorationAreas(canvasViewIcons, views);
      const mismatchedAreas = mismatchedAndMatched.mismatched;
      const matchedAreas = mismatchedAndMatched.matched;
      const productName = style?.name || 'product';
      const productColors = style?.colors || [];
      if (clearMismatchedViews) {
        // silently drop sizes
        // if user already got mismatched view error
        droppedSizes = [];
      }
      if (clearSizeDropWarning) {
        droppedSizes = [];
      }
      const mismatchedNoPhotography = checkAdditionalAreas(
        mismatchedAreas,
        style
      );

      if (mismatchedAreas.length === 0 || clearMismatchedViews) {
        dispatch({
          type: ProductTypes.SWAP_PRODUCT,
          payload: {
            designId,
            changedProductCategory,
            constraints,
            guidelines,
            oldConstraints,
            decorationMethod,
            nextStyleId,
            previousStyleId,
            previousColorId,
            nextColorIds,
            validSizes,
            nextCategoryPath,
            sponsorship: style?.sponsorship,
            newViewIcons,
            canvasViewIcons,
            views,
            scales,
            oldScales,
            isSwappingEmbroidToPrintVariant,
            isInitialLabLoad,
            rearrangeIcons,
            styleType,
            useSpecialGuidelineOnIconRearranger,
            preventHighInkColorAlert
          },
          meta: {
            isAD,
            clearPreviousProducts,
            confirmationRequired: droppedSizes.length,
            fireAnalytics,
            nextColorIds,
            nextStyleId,
            redoUndoable,
            notification: {
              key: NotificationConstants.PERSONALIZATION_NOT_SUPPORTED_WARNING
            }
          }
        });
        dispatch(
          addProductToFundraising({
            styleId: nextStyleId,
            colorId: nextColorIds[previousColorId]
          })
        );
      } else {
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.SWAP_PRODUCT_MISMATCHED_AREAS,
            payload: {
              matchedAreas,
              mismatchedAreas,
              mismatchedNoPhotography,
              previousColorId,
              previousStyleId,
              nextProduct,
              productName,
              productColors
            }
          })
        );
      }
      dispatch(updateSleevePrintOverride());
      // we only want to apply any changes if the decoration override feature flag is enabled
      const isDecorationOverrideEnabled = decorationOverridesEnabled(state);
      if (isDecorationOverrideEnabled) {
        const currentDecoOverride = getDecorationOverrideColor(state);
        dispatch(
          updateIconColorsBasedOnDecorationOverride(
            Boolean(currentDecoOverride)
          )
        );
      }
    }
  };
}

function checkAdditionalAreas(mismatchedAreas, style) {
  const additionalAreas = style.additional_print_areas || [];
  const mismatchedViewNames = mismatchedAreas.map(id => {
    return ID_VIEW_MAP[id];
  });
  const mismatchedNoPhotography = [];
  for (const mismatchedView of mismatchedViewNames) {
    if (additionalAreas.includes('sleeve')) {
      if (mismatchedView === 'leftSleeve') {
        mismatchedNoPhotography.push('left sleeve');
      } else if (mismatchedView === 'rightSleeve') {
        mismatchedNoPhotography.push('right sleeve');
      }
    } else if (additionalAreas.includes(mismatchedView)) {
      mismatchedNoPhotography.push(mismatchedView);
    }
  }
  return mismatchedNoPhotography;
}

export function checkDecorationAreas(canvasViewIcons, views) {
  const newViewIds = views.map(view => {
    return view.id;
  });
  const oldViewIds = [...canvasViewIcons.keys()];
  const mismatched = [];
  const matched = [];
  oldViewIds.map(viewId => {
    if (!newViewIds.includes(viewId)) {
      mismatched.push(viewId);
    } else {
      matched.push(viewId);
    }
  });
  return {matched, mismatched};
}

export function validateActiveProducts() {
  return (dispatch, getState) => {
    let allProductsActive = true;
    allProductsActive = validateStylesActive(dispatch, getState);
    if (allProductsActive) {
      allProductsActive = validateColorsActive(dispatch, getState);
    }
    return allProductsActive;
  };
}

function validateStylesActive(dispatch, getState) {
  const state = getState();
  let validated = true;
  const selectedProducts = getSelectedProducts(state);
  selectedProducts.forEach(product => {
    if (product?.status !== 'active') {
      const selectedStyleColors = getColorSelectionsForStyle(
        state,
        product?.id
      );
      dispatch(discontinuedProduct());
      dispatch(selectProduct(selectedStyleColors[0]));
      dispatch(
        NotificationActions.error({
          key: NotificationConstants.INACTIVE_PRODUCT,
          product: product
        })
      );
      validated = false;
      return;
    }
  });
  return validated;
}

function validateColorsActive(dispatch, getState) {
  const state = getState();
  const selectedColors = getSelectedColors(state);
  const selectedProducts = getSelectedProducts(state);
  let validated = true;
  selectedColors.forEach((color, i) => {
    if (!color) {
      validated = false;
    } else if (color.status !== 'active') {
      const alternative = getColor(state, {
        styleId: selectedProducts.get(i).id,
        colorId: color.alternative_id
      });
      dispatch(discontinuedProductColor());
      dispatch(selectProduct(color.id));
      dispatch(
        NotificationActions.error({
          key: NotificationConstants.RETIRED_PRODUCT_COLOR,
          alternative,
          color
        })
      );
      validated = false;
    }
  });
  return validated;
}

export function removeSleeveDesign() {
  return dispatch => {
    dispatch(
      removeIconsSelectedProductView({
        productViewId: VIEW_ID_MAP.leftSleeve
      })
    );
    dispatch(
      removeIconsSelectedProductView({
        productViewId: VIEW_ID_MAP.rightSleeve
      })
    );
  };
}

export function changeProductView({id}) {
  return (dispatch, getState) => {
    dispatch(changeView(id));
  };
}

export function removeIconsSelectedProductView({productViewId}) {
  return (dispatch, getState) => {
    if (!productViewId) return;

    const icons = getIconsArray(getState());
    const selectedViewIcons = getProductViewIcons({icons, productViewId});

    if (!selectedViewIcons) return;

    dispatch(
      selectIcons({
        iconIds: selectedViewIcons,
        viewId: productViewId
      })
    );
    dispatch(remove());
  };
}

export function dropSleeveViews(savings) {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(removeSleeveDesign());
    dispatch(
      updateDroppedView({
        ...getDroppedViewInfo(state)({
          productViewId: VIEW_ID_MAP.rightSleeve
        }),
        savings
      })
    );
    dispatch(
      selectRemainingProductView({droppedViewId: VIEW_ID_MAP.rightSleeve})
    );
    dispatch(sleevePrintRemoveDesign());
  };
}

export function selectRemainingProductView({droppedViewId}) {
  return (dispatch, getState) => {
    if (!droppedViewId) return;

    const remainingView = getRemainingProductView(getState())({
      droppedProductViewId: droppedViewId
    });

    if (!remainingView?.id) return;

    dispatch(changeView(remainingView.id));
  };
}

export function handleInvalidProductImage(productId, viewId) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedView = getSelectedView(state);

    if (isSleeveView({viewId})) {
      if (isSleeveView({viewId: selectedView?.id})) {
        dispatch(changeView(VIEW_ID_MAP.front));
      }
      if (!hasSleeveDesign(state) && isSingleStyle(state)) {
        dispatch(setSleevePrintOverride(false));
      }
      if (!hasSleeveProductImageIssue(state)) {
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.FAILED_LOAD_SLEEVE_PRODUCT_IMAGE
          })
        );
      }
    }
    dispatch({
      type: ProductTypes.UPDATE_HAS_INVALID_PRODUCT_IMAGES,
      payload: {
        productId,
        viewId
      }
    });
  };
}
